import React, { useCallback, useEffect, useRef, useState } from "react";

import styles from "./DualScrollTable.module.scss";

const MIN_THUMB_WIDTH = 40;

const getMetrics = (body, track) => {
    if (!body || !track) return null;
    const trackWidth = track.clientWidth;
    const maxScroll = body.scrollWidth - body.clientWidth;
    const thumbWidth = Math.min(
        trackWidth,
        Math.max(MIN_THUMB_WIDTH, (trackWidth * body.clientWidth) / body.scrollWidth)
    );
    return { trackWidth, maxScroll, thumbWidth, travel: trackWidth - thumbWidth };
};

// Horizontal scroll container with custom-drawn scrollbars above and below the content,
// so wide tables can be scrolled sideways without reaching the bottom.
// The bars are drawn (the native one is hidden) so they look the same in every browser,
// including Firefox where native overlay scrollbars auto-hide.
const DualScrollTable = ({ children, className = "" }) => {
    const bodyRef = useRef(null);
    const topTrackRef = useRef(null);
    const topThumbRef = useRef(null);
    const bottomTrackRef = useRef(null);
    const bottomThumbRef = useRef(null);
    const dragRef = useRef(null);
    const [hasOverflow, setHasOverflow] = useState(false);
    const [dragging, setDragging] = useState(false);

    // Position the thumbs directly through the DOM to avoid re-rendering the table on scroll.
    const updateThumbs = useCallback(() => {
        const body = bodyRef.current;
        [
            [topTrackRef.current, topThumbRef.current],
            [bottomTrackRef.current, bottomThumbRef.current],
        ].forEach(([track, thumb]) => {
            const metrics = getMetrics(body, track);
            if (!metrics || !thumb) return;
            const ratio = metrics.maxScroll > 0 ? body.scrollLeft / metrics.maxScroll : 0;
            thumb.style.width = `${metrics.thumbWidth}px`;
            thumb.style.transform = `translateX(${ratio * metrics.travel}px)`;
        });
    }, []);

    useEffect(() => {
        const body = bodyRef.current;
        if (!body) return undefined;

        let frame = null;

        const measure = () => {
            setHasOverflow(body.scrollWidth > body.clientWidth + 1);
            updateThumbs();
        };

        // Measure on the next frame (avoids "ResizeObserver loop" errors and layout thrashing)
        const scheduleMeasure = () => {
            if (frame !== null) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                frame = null;
                measure();
            });
        };

        measure();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", scheduleMeasure);
            return () => {
                window.removeEventListener("resize", scheduleMeasure);
                if (frame !== null) cancelAnimationFrame(frame);
            };
        }

        // Watch both the viewport (container) and the content (table) size.
        const observer = new ResizeObserver(scheduleMeasure);
        observer.observe(body);
        Array.from(body.children).forEach((child) => observer.observe(child));
        return () => {
            observer.disconnect();
            if (frame !== null) cancelAnimationFrame(frame);
        };
    }, [updateThumbs]);

    // The tracks have no size while hidden, so re-measure once they become visible.
    useEffect(() => {
        if (hasOverflow) updateThumbs();
    }, [hasOverflow, updateThumbs]);

    const handleThumbPointerDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = {
            track: e.currentTarget.parentElement,
            startX: e.clientX,
            startScroll: bodyRef.current.scrollLeft,
        };
        setDragging(true);
    };

    const handleThumbPointerMove = (e) => {
        const drag = dragRef.current;
        const body = bodyRef.current;
        const metrics = drag && getMetrics(body, drag.track);
        if (!metrics || metrics.travel <= 0) return;
        body.scrollLeft = drag.startScroll + ((e.clientX - drag.startX) * metrics.maxScroll) / metrics.travel;
    };

    const handleThumbPointerUp = () => {
        dragRef.current = null;
        setDragging(false);
    };

    // Clicking the empty track pages left/right, like a native scrollbar.
    const handleTrackPointerDown = (e) => {
        if (e.button !== 0) return;
        const body = bodyRef.current;
        const thumb = e.currentTarget.firstElementChild;
        if (!body || !thumb) return;
        const thumbRect = thumb.getBoundingClientRect();
        const direction = e.clientX < thumbRect.left ? -1 : 1;
        body.scrollBy({ left: direction * body.clientWidth * 0.8, behavior: "smooth" });
    };

    // Horizontal trackpad / shift+wheel gestures over a bar scroll the table.
    const handleTrackWheel = (e) => {
        const delta = e.deltaX || (e.shiftKey ? e.deltaY : 0);
        if (delta && bodyRef.current) bodyRef.current.scrollLeft += delta;
    };

    const renderTrack = (trackRef, thumbRef, positionClass) => (
        <div
            ref={trackRef}
            className={`${styles.track} ${positionClass} ${hasOverflow ? "" : styles.hidden} ${dragging ? styles.dragging : ""}`}
            onPointerDown={handleTrackPointerDown}
            onWheel={handleTrackWheel}
            aria-hidden="true"
        >
            <div
                ref={thumbRef}
                className={styles.thumb}
                onPointerDown={handleThumbPointerDown}
                onPointerMove={handleThumbPointerMove}
                onPointerUp={handleThumbPointerUp}
                onPointerCancel={handleThumbPointerUp}
            />
        </div>
    );

    return (
        <div className={`${styles.wrapper} ${className}`}>
            {renderTrack(topTrackRef, topThumbRef, styles.trackTop)}
            <div ref={bodyRef} className={styles.body} onScroll={updateThumbs}>
                {children}
            </div>
            {renderTrack(bottomTrackRef, bottomThumbRef, styles.trackBottom)}
        </div>
    );
};

export default DualScrollTable;
